import { createProgressiveWebGLRenderer } from './fractalProgressiveWebGL';
import { createRenderBudget } from './renderBudget';
import { drawMandelbrotFractalWebGL } from './fractalWebGL';

const sameRanges = (a, b) => a && b && ['xRange', 'yRange'].every(axis =>
  a[axis]?.[0] === b[axis]?.[0] && a[axis]?.[1] === b[axis]?.[1]);

// Presentation follows RAF; small compute submissions retire on browser tasks.
// The two clocks share an admission ledger, rather than queuing a full shader
// behind every input or limiting useful work to one tiny tile per display frame.
export function createNumberMotionLoop(canvas, { onFrame, onError, isVisible,
  now = () => performance.now() } = {}) {
  // Larger spatial regions cover the pointer neighborhood in one result.
  // Recurrence remains resumable; its measured quantum shrinks independently.
  let tileSize = 256;
  let renderer = createProgressiveWebGLRenderer(canvas, { tileSize });
  if (!renderer) return null;
  const budget = createRenderBudget(Symbol('number-motion'));
  let ranges = null, palette = null, interactive = false;
  let requestedAt = 0, frame = null, timer = null, ticket = null;
  let disposed = false, suspended = false, calibration = 6;
  let lastFrameTime = 0, completion = null, notifiedFrame = null;
  let taskQueued = false, unsignaledPolls = 0;
  let pollStartedAt = null, delayedPolls = 0;
  let pollingCpuMs = 0, lastRetiredGpuMs = null;
  let requestOptions = {}, rebuilding = null, resourceRebuilds = 0;
  let heldRetirementTiming = null;
  let externalPresentationPending = false;
  const taskChannel = typeof MessageChannel === 'function' ? new MessageChannel() : null;
  if (taskChannel) taskChannel.port1.onmessage = () => {
    taskQueued = false;
    if (!disposed) pump();
  };

  const visible = () => !disposed && !suspended && !document.hidden &&
    (isVisible ? isVisible(canvas) : true);
  const pending = () => !!ranges && (!!rebuilding || !sameRanges(renderer.exactFrame, ranges) || !!renderer.inFlight);

  function record() {
    canvas._fractalMotionStats = { renderer: 'progressive-webgl', ...budget.snapshot(),
      ...renderer.stats, requestedCameraAt: requestedAt,
      tileSize, resourceRebuilds, preparingTileSize: rebuilding?.tileSize ?? null,
      requestedRanges: ranges, presentedAt: lastFrameTime,
      timingNote: 'GPU timers or labeled fence estimates; RAF submission is not display latency.' };
  }

  function retire(result) {
    if (result?.retired && ticket) {
      const timing = result.timing || result.lastTiming || renderer.stats?.lastTiming || {};
      if (renderer.stats?.requiresSmallerTile) heldRetirementTiming = timing;
      else {
        budget.retireSubmission(ticket, { gpuMs: timing.gpuMs,
          fenceMs: timing.fenceMs ?? Math.max(0, now() - ticket.startedAt) });
        ticket = null;
      }
      unsignaledPolls = 0;
      pollStartedAt = null;
      delayedPolls = 0;
      lastRetiredGpuMs = Number.isFinite(timing.gpuMs) && timing.gpuMs >= 0 ? timing.gpuMs : null;
      if (timer !== null) { clearTimeout(timer); timer = null; }
    }
    if (result?.complete && result.exactFrame) completion = result.exactFrame;
    record();
  }

  function fail(error) {
    if (disposed) return;
    api.dispose();
    onError?.(error);
  }

  function presentRetainedSource() {
    const source = canvas._fractalZoomCache?.frame;
    if (!source || !ranges || source.paletteKey !== JSON.stringify(palette || {}) ||
        source.width !== canvas.width || source.height !== canvas.height) return false;
    // projectionOnly prevents this temporary presentation from falling through
    // to an expensive full-grid shader if retained resources are unavailable.
    return drawMandelbrotFractalWebGL(canvas, ranges.xRange, ranges.yRange, palette,
      { interactiveZoom: true, projectionOnly: true });
  }

  function presentRebuilding(frameIntervalMs) {
    if (rebuilding.paletteKey === JSON.stringify(palette || {})) {
      const result = rebuilding.renderer.present(ranges.xRange, ranges.yRange, { frameIntervalMs });
      if (result !== false && result !== undefined) return result;
    }
    return presentRetainedSource();
  }

  function releaseRetainedAccepted(state) {
    const retained = state.retainedAccepted;
    state.retainedAccepted = null;
    retained?.release?.();
  }

  function finishRebuild(state) {
    if (disposed || rebuilding !== state || !state.retired || !visible()) return;
    try {
      // Old iteration state has physically retired. Reuse its accepted atlas
      // so native coverage and original camera timestamps survive subdivision.
      const next = createProgressiveWebGLRenderer(canvas, { tileSize: state.tileSize,
        ...(state.retainedAccepted ? { retainedAccepted: state.retainedAccepted } : {}) });
      if (!next) throw new Error('Bounded GPU work is unavailable at the smaller tile size');
      state.retainedAccepted = null;
      renderer = next;
      tileSize = state.tileSize;
      resourceRebuilds++;
      rebuilding = null;
      completion = null;
      unsignaledPolls = 0;
      renderer.request(ranges.xRange, ranges.yRange, palette,
        { ...requestOptions, cameraRequestedAt: requestedAt });
      record();
      scheduleFrame();
    } catch (error) { fail(error); }
  }

  function rebuildSmaller() {
    if (rebuilding || !renderer.stats?.requiresSmallerTile) return false;
    const suggested = renderer.stats.suggestedTileSize;
    if (!Number.isInteger(suggested) || suggested < 16 || suggested >= tileSize) {
      throw new Error('GPU work still exceeds the budget at the smallest supported tile size');
    }
    const state = { renderer, tileSize: suggested, retired: false, ticket,
      timing: heldRetirementTiming, promise: null, retainedAccepted: null,
      paletteKey: JSON.stringify(palette || {}) };
    rebuilding = state;
    ticket = null;
    heldRetirementTiming = null;
    completion = null;
    // Disposal owns nonblocking fence polling. The old budget slot remains
    // occupied until that promise acknowledges physical resource retirement.
    state.promise = Promise.resolve().then(() => state.renderer.dispose({ retainAccepted: true })).then(retainedAccepted => {
      if (state.ticket) budget.retireSubmission(state.ticket, state.timing || {});
      state.ticket = null;
      state.retired = true;
      state.retainedAccepted = retainedAccepted || null;
      if (disposed) { releaseRetainedAccepted(state); return; }
      finishRebuild(state);
    }, error => { if (!disposed) fail(error); });
    record();
    scheduleFrame();
    return true;
  }

  function scheduleFrame() {
    if (frame !== null || externalPresentationPending || !visible() || !ranges) return;
    frame = requestAnimationFrame(presentFrame);
  }

  function schedulePump(delay = 0) {
    if (timer !== null || taskQueued || disposed || rebuilding || (!visible() && !renderer.inFlight)) return;
    const queue = () => {
      if (taskChannel) {
        taskQueued = true;
        taskChannel.port2.postMessage(0);
      } else pump();
    };
    if (taskChannel && delay === 0) queue();
    else timer = setTimeout(() => { timer = null; queue(); }, delay);
  }

  function pollRetirement() {
    const startedAt = now();
    const result = renderer.step({ allowSubmission: false, budgetMs: 0,
      frameIntervalMs: budget.snapshot().frameIntervalMs, interactive });
    retire(result);
    pollingCpuMs += Math.max(0, now() - startedAt);
    if (result?.failed || renderer.failed) throw renderer.failed instanceof Error
      ? renderer.failed : new Error('Progressive GPU rendering failed');
    return result;
  }

  function pump() {
    if (disposed || rebuilding) return;
    try {
      const result = pollRetirement();
      if (rebuildSmaller()) return;
      if (result?.requiresPresentation) { scheduleFrame(); return; }
      if (renderer.inFlight || result?.inFlight) {
        unsignaledPolls++;
        if (pollStartedAt === null) pollStartedAt = now();
        // Yield to browser tasks while short GPU slices retire. Longer work
        // backs off instead of spinning; timer callbacks return through the
        // channel so the browser's nested-timer clamp cannot throttle every
        // later small submission to one per four milliseconds.
        // Task duration varies: four short callbacks can precede even a cheap
        // 0.2 ms GPU slice. Bound this cooperative polling window by both wall
        // time and task count, rather than inserting a timer after four calls.
        const intervalMs = budget.snapshot().frameIntervalMs;
        const pollWindowMs = Math.min(0.75, intervalMs / 16);
        // Charge actual polling CPU across every slice in this display interval.
        // Cheap retirements must not restart an unbounded main-thread allowance.
        const pollingCpuBudgetMs = Math.min(0.5, intervalMs / 32);
        const shortPoll = taskChannel && unsignaledPolls < 16 &&
          now() - pollStartedAt < pollWindowMs && pollingCpuMs < pollingCpuBudgetMs;
        // A measured cheap GPU packet can still expose its fence a few tasks
        // later. Keep timer polling cooperative without growing that delay to
        // four milliseconds. Stale cheap history expires after two intervals.
        const cheapFence = lastRetiredGpuMs !== null && lastRetiredGpuMs <= 1 &&
          now() - (ticket?.startedAt ?? pollStartedAt) < 2 * intervalMs;
        schedulePump(shortPoll ? 0 : taskChannel ? (cheapFence ? 1 : Math.min(4, ++delayedPolls)) : 1);
        return;
      }
      if (!visible()) { budget.release(); return; }
      if (result?.accepted || result?.complete) scheduleFrame();
      const hasWork = pending() || result?.pending;
      if (!hasWork) { scheduleFrame(); return; }
      if (!renderer.ready) { scheduleFrame(); return; }
      if (!interactive && calibration > 0) { scheduleFrame(); return; }
      // Completion can remove an older continuation between display frames.
      // Refresh reservation eligibility without manufacturing a new interval
      // or restarting the latest camera's original deadline.
      const snapshot = budget.updatePending({ latest: hasWork,
        progress: !!renderer.progressPending });
      // Unknown refresh rates start conservatively. Idle calibration below
      // establishes F before any noninteractive compute occupies the GPU.
      const submissionBudgetMs = calibration > 0
        ? Math.min(2, snapshot.submissionBudgetMs) : snapshot.submissionBudgetMs;
      if (!(submissionBudgetMs > 0)) { scheduleFrame(); return; }
      const requestedKind = snapshot.dueKind || (renderer.progressPending &&
        renderer.stats?.nextKind === 'progress' ? 'progress' : 'latest');
      const slicePlan = renderer.nextSlicePlan?.({ kind: requestedKind, budgetMs: submissionBudgetMs });
      const kind = slicePlan?.kind || requestedKind;
      const phaseEstimateMs = slicePlan?.estimateMs ??
        renderer.estimateNextSlice?.({ kind, budgetMs: submissionBudgetMs });
      const nextSliceEstimateMs = renderer.nextSliceEstimateMs;
      const estimatedSliceMs = renderer.stats?.estimatedSliceMs;
      const fullGridEstimateMs = renderer.stats?.fullGridEstimateMs;
      const estimateMs = Math.min(submissionBudgetMs, Math.max(0.1,
        Number.isFinite(phaseEstimateMs) ? phaseEstimateMs :
          Number.isFinite(nextSliceEstimateMs) ? nextSliceEstimateMs : Math.max(
          Number.isFinite(estimatedSliceMs) ? estimatedSliceMs : submissionBudgetMs,
          Number.isFinite(fullGridEstimateMs) && fullGridEstimateMs <= submissionBudgetMs
            ? fullGridEstimateMs : 0)));
      const next = budget.beginSubmission({ kind, estimateMs });
      if (!next) { scheduleFrame(); return; }
      next.startedAt = now();
      ticket = next;
      unsignaledPolls = 0;
      pollStartedAt = now();
      delayedPolls = 0;
      const submitted = renderer.step({ allowSubmission: true,
        budgetMs: submissionBudgetMs, frameIntervalMs: snapshot.frameIntervalMs,
        interactive, kind });
      if (!submitted?.submitted) {
        budget.retireSubmission(ticket, { gpuMs: 0 });
        ticket = null;
      }
      retire(submitted);
      record();
      if (submitted?.requiresPresentation) { scheduleFrame(); return; }
      if (submitted?.accepted || submitted?.complete) scheduleFrame();
      if (renderer.inFlight || submitted?.inFlight) schedulePump(taskChannel ? 0 : 1);
      else if (submitted?.submitted) schedulePump();
      else scheduleFrame();
    } catch (error) { fail(error); }
  }

  function presentFrame(time) {
    frame = null;
    if (!visible() || !ranges) return;
    lastFrameTime = time;
    try {
      pollingCpuMs = 0;
      // The browser may deliver RAF before a backed-off timer. Retire already
      // finished work once before composing so this frame can use its native
      // anchor. Poll only an existing fence: no initialization or new compute.
      if (!rebuilding && renderer.inFlight) {
        pollRetirement();
        rebuildSmaller();
      }
      const hasWork = pending();
      const calibrating = !interactive && calibration > 0;
      const snapshot = budget.tick(time, { visible: true, interactive,
        active: interactive && now() - requestedAt <= 2 * budget.snapshot().frameIntervalMs,
        pending: { latest: hasWork && !calibrating,
          progress: !rebuilding && !!renderer.progressPending && !calibrating } });
      const presentation = rebuilding ? presentRebuilding(snapshot.frameIntervalMs)
        : renderer.present(ranges.xRange, ranges.yRange, { frameIntervalMs: snapshot.frameIntervalMs });
      if (presentation !== false) {
        // Composition is mandatory and separate from fenced recurrence/color
        // work. Prior asynchronous timing informs admission without charging
        // compute twice or waiting for a query. An idle draw can retire much
        // later; its original submission must still be recent to count as the
        // current presentation's actual cost.
        const compositionGpuMs = renderer.stats?.compositionGpuMs;
        const compositionEstimateMs = renderer.stats?.compositionEstimateMs;
        const compositionStartedAt = renderer.stats?.lastCompositionTiming?.queryStartedAt;
        const staleComposition = Number.isFinite(compositionStartedAt) &&
          now() - compositionStartedAt > 2 * snapshot.frameIntervalMs;
        if (staleComposition) {
          const recentEstimateMs = renderer.stats?.compositionRecentEstimateMs;
          budget.observePresentation(Number.isFinite(recentEstimateMs) && recentEstimateMs >= 0
            ? recentEstimateMs : 0.35, { estimate: true });
        } else if (Number.isFinite(compositionGpuMs) && compositionGpuMs >= 0) {
          budget.observePresentation(compositionGpuMs, { estimate: false });
        } else if (Number.isFinite(compositionEstimateMs) && compositionEstimateMs >= 0) {
          budget.observePresentation(compositionEstimateMs, { estimate: true });
        }
      }
      if (presentation?.exactFrame) completion = presentation.exactFrame;
      const exact = rebuilding ? null : completion || renderer.exactFrame;
      if (exact && sameRanges(exact, ranges) && !renderer.inFlight && exact !== notifiedFrame) {
        onFrame?.(exact);
        notifiedFrame = exact;
        completion = null;
      }
      record();
      if (calibrating) calibration--;
      else if (hasWork) schedulePump();
      if (hasWork || renderer.inFlight || (!interactive && calibration > 0)) scheduleFrame();
      canvas._fractalMotionStats.submissionBudgetMs = snapshot.submissionBudgetMs;
    } catch (error) { fail(error); }
  }

  const api = {
    request(next, params, options = {}) {
      if (disposed) return false;
      externalPresentationPending = false;
      suspended = false;
      ranges = { xRange: [...next.xRange], yRange: [...next.yRange] };
      palette = params;
      interactive = !!options.interactive;
      requestedAt = now();
      requestOptions = { ...options, anchor: options.anchor &&
        (Array.isArray(options.anchor) ? [...options.anchor] : { ...options.anchor }) };
      if (rebuilding) finishRebuild(rebuilding);
      else renderer.request(ranges.xRange, ranges.yRange, palette,
        { ...requestOptions, cameraRequestedAt: requestedAt });
      completion = null;
      if (Number.isFinite(options.presentationTime)) {
        if (frame !== null) cancelAnimationFrame(frame);
        frame = null;
        presentFrame(options.presentationTime);
      } else scheduleFrame();
      return true;
    },
    reservePresentation() {
      if (disposed) return false;
      externalPresentationPending = true;
      if (frame !== null) cancelAnimationFrame(frame);
      frame = null;
      return true;
    },
    resume() { suspended = false; if (rebuilding) finishRebuild(rebuilding); scheduleFrame(); },
    suspend({ restoreSource = true } = {}) {
      suspended = true;
      externalPresentationPending = false;
      if (frame !== null) cancelAnimationFrame(frame);
      frame = null;
      budget.release();
      const source = canvas._fractalZoomCache?.frame;
      if (restoreSource && source && !rebuilding) renderer.present(source.xRange, source.yRange,
        { sourceOnly: true, frameIntervalMs: budget.snapshot().frameIntervalMs });
      if (renderer.inFlight) schedulePump(1);
    },
    dispose() {
      if (disposed) return;
      disposed = true;
      if (frame !== null) cancelAnimationFrame(frame);
      if (timer !== null) clearTimeout(timer);
      frame = timer = null;
      taskQueued = false;
      if (taskChannel) {
        taskChannel.port1.onmessage = null;
        taskChannel.port1.close();
        taskChannel.port2.close();
      }
      budget.dispose();
      if (rebuilding) { releaseRetainedAccepted(rebuilding); return; }
      const retiring = ticket;
      const retirementTiming = heldRetirementTiming;
      ticket = null;
      Promise.resolve(renderer.dispose()).then(() => {
        if (retiring) budget.retireSubmission(retiring, retirementTiming || {});
      }, () => {
        if (retiring) budget.retireSubmission(retiring, retirementTiming || {});
      });
    },
    get renderer() { return renderer; },
    get stats() { return canvas._fractalMotionStats; },
  };
  return api;
}
